import { useCartStore } from "~/stores/CartStore"
import { useNuxtApp } from "#app"
import { storeToRefs } from "pinia"
import { usePaysafeStore } from "~/stores/PaysafeStore"
import { useCurrencyStore } from "~/stores/CurrencyStore"
import { useProductStore } from "~/stores/ProductStore"
import { useOrderStore } from "~/stores/OrderStore"
import { FLUSH_CART_STORE, SHOW_ERROR } from "assets/events/errors.js"
import { QTY_ADDED_TO_CART } from "assets/events/api.js"
import CartItem from "~/models/CartItem"
import {
	CART_CLEARED,
	UPDATE_BILLING_COUNTRY,
	MODIFIED_RESERVED_ITEM,
	REFRESH_CART,
	UPDATE_SHIPPING_COUNTRY,
} from "assets/events/cart.js"
import confetti from "canvas-confetti"

export default defineNuxtPlugin((nuxtApp) => {
	const runtimeConfig = useRuntimeConfig()
	const gtm = useGtm()
	const seatsIoEnabled = !!runtimeConfig.public.SEATS_IO

	const cartStore = useCartStore()
	const { cartId, billingAndShippingAreSame } = storeToRefs(cartStore)
	const { $apiHelper, $cartApiService, $cartTimeout, $eventBus, $messages, $countryAndStatesService, $currency } =
		useNuxtApp()

	const currencyStore = useCurrencyStore()
	const { currency } = storeToRefs(currencyStore)
	const paysafeStore = usePaysafeStore()
	const productStore = useProductStore()
	const orderStore = useOrderStore()

	let isPlacingOrder = false

	const callGetCart = async () => {
		$apiHelper.$showLoader()
		const { data, pending, status } = await $cartApiService.$getCart(cartId.value)

		$apiHelper.$handleResponse(pending, status, data, (response) => {
			const cartData = response.cart
			handleGetCartResponse(cartData)
		})
	}

	const getCartMetaRowFromResponse = (response) => {
		if (response && Array.isArray(response)) {
			response = response.filter((item) => item.sk === "meta")
			return response.length ? response[0] : []
		} else {
			throw createError({ statusCode: 500, statusMessage: "Got an invalid response from API" })
		}
	}

	//Parse out the cartId from the response, AWS's pk pattern forces the use of hardcoded string split here
	const storeCartId = (response) => {
		const cartPk = getCartMetaRowFromResponse(response).pk
		const splitCartPk = cartPk.split("#")
		const cartId = splitCartPk[1]
		cartStore.setCartId(cartId)
		return cartId
	}

	//Get the Cart expiry from the response
	const storeCartExpiry = (response) => {
		const expiresAtEpoch = getCartMetaRowFromResponse(response).expiresAt
		setCartExpiry(expiresAtEpoch)
	}

	//Utilize the watcher in CartTimeoutService
	const setCartExpiry = (expiresAtEpoch) => {
		let expirationDate = new Date(0)
		// expiresAtEpoch -= 570 //FOR TESTING: Set to 6 minutes
		expirationDate.setUTCSeconds(expiresAtEpoch)
		cartStore.setExpiresAt(expirationDate)
	}

	//Gets all cart meta data except cartId and expiresAt
	const storeCartMeta = (response) => {
		const cartMetaRow = getCartMetaRowFromResponse(response)
		const subtotal = cartMetaRow.subtotal
		const total = cartMetaRow.total
		const fees = cartMetaRow.fees
		const refundableBookingCost = cartMetaRow.refundableBookingCost
		const isRefundableBookingSelected = cartMetaRow.isRefundableBookingSelected
		const installments = Object.values(cartMetaRow.installments)
		const appliedPromocode = cartMetaRow.appliedPromocode
		const appliedCredits = cartMetaRow.appliedCredits || []
		const discountedAmount = cartMetaRow.discountedAmount
		const creditAppliedTotal = cartMetaRow.creditAppliedTotal || 0
		const exchangeRate = cartMetaRow.exchangeRate
		const currency = cartMetaRow.currency

		cartStore.setSubTotal(subtotal)
		cartStore.setTotal(total)
		cartStore.setFees(fees)
		cartStore.setRefundableBookingCost(refundableBookingCost)
		cartStore.setIsRefundableBookingSelected(isRefundableBookingSelected)
		cartStore.setInstallments(installments)
		cartStore.setAppliedPromocode(appliedPromocode)
		cartStore.setAppliedCredits(appliedCredits)
		cartStore.setDiscountedAmount(discountedAmount)
		cartStore.setCreditAppliedTotal(creditAppliedTotal)
		cartStore.setExchangeRate(exchangeRate)
		cartStore.setCurrency(currency)
	}

	const updateCurrencyAndBillingInfo = (currencyCode, shouldUpdateBilling = true) => {
		const currencyObj = $currency.$getCurrencyByCode(currencyCode)
		currencyStore.switchCurrency(currencyObj)

		if (shouldUpdateBilling && cartStore.billingCountry !== currencyObj.countryIso3) {
			cartStore.setBillingCountry(currencyObj.countryIso3)
			cartStore.setBillingState("")
			cartStore.setBillingZipcode("")

			if (billingAndShippingAreSame.value) {
				cartStore.setShippingCountry(currencyObj.countryIso3)
				cartStore.setShippingState("")
				cartStore.setShippingZipcode("")
				$eventBus.$emit(UPDATE_SHIPPING_COUNTRY)
			}
		}
	}

	//Get and store all the cart items from the response
	const storeCartItems = (response) => {
		const cartItems = getCartItemsFromResponse(response)
		//CartItems can be 0 if the user updates the quantity to 0 using the counter buttons
		if (cartItems.length <= 0) {
			cartStore.resetCartMetaAndItems()
		} else {
			cartStore.resetCartItems()
			cartItems.forEach((item) => {
				const splitProductSk = item.sk.split("#")
				item.id = splitProductSk[1]
				const cartItem = CartItem.createFromPartial(item)
				cartStore.addItem(cartItem)
			})
		}
	}

	const getCartItemsFromResponse = (response) => {
		if (response && Array.isArray(response)) {
			return response.filter((item) => item.sk !== "meta")
		} else {
			throw createError({ statusCode: 500, statusMessage: "Got an invalid response from API" })
		}
	}

	const handleGetCartResponse = (cartData) => {
		storeCartExpiry(cartData)
		storeCartMeta(cartData)
		storeCartItems(cartData)
	}

	const handleAddCartItemResponse = (cartData) => {
		storeCartId(cartData)
		storeCartExpiry(cartData)
		storeCartMeta(cartData)
		storeCartItems(cartData)
	}

	const handleUpdateCartItemResponse = (cartData) => {
		if (clearCartIfEmpty(cartData)) {
			return
		}

		storeCartMeta(cartData)
		storeCartItems(cartData)
	}

	const handleDeleteCartItemResponse = (productId, cartData) => {
		const removedItem = cartStore.removeItem(productId)

		if (clearCartIfEmpty(cartData)) {
			return
		}

		storeCartMeta(cartData)
		storeCartItems(cartData)
	}

	const clearCartIfEmpty = (response) => {
		if (response && Array.isArray(response) && response.length === 0) {
			cartStore.resetCartMetaAndItems()
			return true
		}
		return false
	}

	const handlePlaceOrderResponse = (response) => {
		const cartData = response.cart
		const cartMetaRow = getCartMetaRowFromResponse(cartData)
		trackOrderForAnalytics(cartMetaRow)
		orderStore.$reset()

		orderStore.setOrderId(cartMetaRow.orderId)
		orderStore.setFirstName(cartStore.billingFirstName)
		orderStore.setLastName(cartStore.billingLastName)
		orderStore.setEmail(cartStore.shippingEmail)
		orderStore.setAddress1(cartStore.billingAddress1)
		orderStore.setAddress2(cartStore.billingAddress2)
		orderStore.setCountry(cartStore.billingCountry)
		orderStore.setState(cartStore.billingState)
		orderStore.setCity(cartStore.billingCity)
		orderStore.setZipCode(cartStore.billingZipcode)

		orderStore.setShippingFirstName(cartStore.shippingFirstName)
		orderStore.setShippingLastName(cartStore.shippingLastName)
		orderStore.setShippingAddress1(cartStore.shippingAddress1)
		orderStore.setShippingAddress2(cartStore.shippingAddress2)
		orderStore.setShippingCountry(cartStore.shippingCountry)
		orderStore.setShippingState(cartStore.shippingState)
		orderStore.setShippingCity(cartStore.shippingCity)
		orderStore.setShippingZip(cartStore.shippingZipcode)

		orderStore.setSubTotal(cartMetaRow.subtotal)
		orderStore.setTotal(cartMetaRow.total)
		orderStore.setFees(cartMetaRow.fees)
		orderStore.setRefundableBookingCost(cartMetaRow.refundableBookingCost)
		orderStore.setDiscountedAmount(cartMetaRow.discountedAmount)
		orderStore.setAppliedPromocode(cartMetaRow.appliedPromocode)
		orderStore.setCreditAppliedTotal(cartMetaRow.creditAppliedTotal || 0)
		orderStore.setInstallments(Object.values(cartMetaRow.installments))

		//Note: That there is a lot more card data to be parsed, but it is unused at this time
		const cardData = response.card
		orderStore.setLastFourDigits(cardData && cardData.last_digits ? cardData.last_digits : "")

		//Note: That there is a lot more order data to be parsed, but it is unused at this time
		const orderData = response.order
		orderStore.setCreatedAt(orderData.created_at)

		const cartItems = getCartItemsFromResponse(cartData)
		cartItems.forEach((item) => {
			const splitProductSk = item.sk.split("#")
			item.id = splitProductSk[1]
			const cartItem = CartItem.createFromPartial(item)
			orderStore.addItem(cartItem)
		})
	}

	const trackOrderForAnalytics = (cartMetaRow) => {
		if (gtm?.options?.enabled) {
			gtm?.dataLayer().push({ transaction_id: cartMetaRow.orderId })
			gtm?.dataLayer().push({ total_amount: cartMetaRow.total })
		}
	}

	const handleExtendCartTimeoutResponse = (response) => {
		//Reset the cart timeout interrupts
		const newCartExpiryEpoch = response.expiresAt
		setCartExpiry(newCartExpiryEpoch)
	}

	const resetCartDataToDefaults = async () => {
		cartStore.$reset()
		$eventBus.$emit(CART_CLEARED)
	}

	const isCartEmpty = () => {
		if (!cartId.value) {
			$eventBus.$emit(SHOW_ERROR, {
				titleText: $messages.$t("noCartExists"),
				bodyText: $messages.$t("noCartItemsBody"),
			})
			return true
		}
		return false
	}

	const showSeatSelector = (productId, currentQuantity) => {
		if (!seatsIoEnabled) {
			return false
		}

		if (currentQuantity <= 0) {
			return false
		}

		const modifiedItem = cartStore.items.get(productId)
		if (modifiedItem.reservationSection) {
			$eventBus.$emit(MODIFIED_RESERVED_ITEM, { productId: productId })
			return true
		} else {
			return false
		}
	}

	nuxtApp.provide("cartService", {
		$init: () => {
			$eventBus.$on(FLUSH_CART_STORE, resetCartDataToDefaults)
			$eventBus.$on(REFRESH_CART, handleGetCartResponse)
		},

		$getCart: callGetCart,

		//Called whenever an item quantity is changed
		//Intended to be used on a product page (Card.vue) where the counters do not show real-time item quantities
		$addCartItemQty: async (id, quantityAdded) => {
			const itemInCart = cartStore.getItem(id)
			const totalQuantity = itemInCart ? itemInCart.quantity + quantityAdded : quantityAdded

			// prevent user from adding too many products
			const productInStore = productStore.getProductById(id)
			if (productInStore && totalQuantity > productInStore.availableQuantity) {
				$apiHelper.$hideLoader()
				$eventBus.$emit(SHOW_ERROR, {
					titleText: $messages.$t("maxReachedTitle"),
					bodyText: $messages.$t("maxReachedBody", { max: productInStore.availableQuantity }),
				})
				return
			}

			$apiHelper.$showLoader()

			const { data, pending, status } = await $cartApiService.$setCartItemQty(
				cartId.value,
				id,
				currency.value.code,
				totalQuantity,
				productInStore.price[currency.value.code]
			)

			$apiHelper.$handleResponse(pending, status, data, (response) => {
				const cartData = response.cart
				handleAddCartItemResponse(cartData)
				$eventBus.$emit(QTY_ADDED_TO_CART, { productId: id })
				if (!showSeatSelector(id, totalQuantity)) {
					confetti()
				}
			})
		},

		//Called whenever an item quantity is changed
		$setCartItemQty: async (productId, quantity) => {
			if (isCartEmpty()) {
				return
			}

			const itemInCart = cartStore.getItem(productId)
			if (itemInCart.quantity === quantity) {
				//The Cart item quantity did not change
				return
			}

			// prevent user from adding too many products
			const productInStore = productStore.getProductById(productId)
			const availableQty = productInStore.availableQuantity
			if (quantity > availableQty) {
				$eventBus.$emit(SHOW_ERROR, {
					titleText: $messages.$t("maxReachedTitle"),
					bodyText: $messages.$t("maxReachedBody", { max: availableQty }),
				})
				return
			}

			$apiHelper.$showLoader()

			const { data, pending, status } = await $cartApiService.$setCartItemQty(
				cartId.value,
				productId,
				currency.value.code,
				quantity,
				productInStore.price[currency.value.code]
			)

			$apiHelper.$handleResponse(pending, status, data, (response) => {
				const cartData = response.cart
				handleUpdateCartItemResponse(cartData)

				if (!showSeatSelector(productId, quantity)) {
					confetti()
				}
			})
		},

		//Called whenever an item removed from the cart
		$deleteCartItem: async (productId) => {
			if (isCartEmpty()) {
				return
			}

			$apiHelper.$showLoader()

			const { data, pending, status } = await $cartApiService.$deleteCartItem(cartId.value, productId)

			$apiHelper.$handleResponse(pending, status, data, (response) => {
				const cartData = response.cart
				handleDeleteCartItemResponse(productId, cartData)
			})
		},

		$updateInstallments: async (numInstallments) => {
			//The user could go to the checkout page when the cart is empty
			if (!cartStore.cartId) {
				return
			}

			$apiHelper.$showLoader()
			const { data, pending, status } = await $cartApiService.$updateInstallments(cartId.value, numInstallments)

			$apiHelper.$handleResponse(pending, status, data, (response) => {
				const cartData = response.cart
				//installments are in the cart meta
				storeCartMeta(cartData)
				//Installment fees may have been added
				storeCartItems(cartData)
			})
		},

		$placeOrder: async (successHandler) => {
			if (isCartEmpty()) {
				return
			}

			if (!isPlacingOrder) {
				isPlacingOrder = true
				//Only allow orders to be placed once per second
				setTimeout(() => {
					isPlacingOrder = false
				}, 1000)
				$apiHelper.$showLoader()

				const cartBody = {
					total: cartStore.total,
					token: paysafeStore.token,
					recaptchaToken: cartStore.captchaToken,
					reservationHoldToken: cartStore.reservationHoldToken,
					user: {
						firstName: cartStore.shippingFirstName,
						lastName: cartStore.shippingLastName,
						billingFirstName: cartStore.billingFirstName,
						billingLastName: cartStore.billingLastName,
						email: cartStore.shippingEmail,
						phone: cartStore.shippingPhone,
						address: cartStore.billingAddress1,
						address2: cartStore.billingAddress2,
						city: cartStore.billingCity,
						state: cartStore.billingState,
						country: $countryAndStatesService.$convertIso3ToIso2(cartStore.billingCountry),
						zip: cartStore.billingZipcode,
						shippingFirstName: cartStore.shippingFirstName,
						shippingLastName: cartStore.shippingLastName,
						shippingAddress: cartStore.shippingAddress1,
						shippingAddress2: cartStore.shippingAddress2,
						shippingCity: cartStore.shippingCity,
						shippingState: cartStore.shippingState,
						shippingCountry: $countryAndStatesService.$convertIso3ToIso2(cartStore.shippingCountry),
						shippingZip: cartStore.shippingZipcode,
					},
				}

				const { data, pending, status } = await $cartApiService.$placeOrder(cartId.value, cartBody)

				$apiHelper.$handleResponse(pending, status, data, (placeOrderRes) => {
					handlePlaceOrderResponse(placeOrderRes)
					if (successHandler && typeof successHandler === "function") {
						successHandler()
					}
				})
			}
		},

		$extendCartTimeout: async () => {
			if (isCartEmpty()) {
				return
			}

			$apiHelper.$showLoader()
			const { data, pending, status } = await $cartApiService.$extendCartTimeout(cartId.value)

			$apiHelper.$handleResponse(pending, status, data, (extendCartRes) => {
				handleExtendCartTimeoutResponse(extendCartRes)
			})
		},

		$updateRefundableBooking: async (isRefundableBookingSelected) => {
			if (!cartId.value) {
				return
			}

			$apiHelper.$showLoader()
			const { data, pending, status } = await $cartApiService.$updateRefundableBooking(
				cartId.value,
				isRefundableBookingSelected
			)

			$apiHelper.$handleResponse(pending, status, data, (response) => {
				cartStore.setIsRefundableBookingSelected(isRefundableBookingSelected)
				const cartData = response.cart
				storeCartMeta(cartData)
			})

			//Revert the refundable booking selection if the API call failed
			if (!pending.value && status.value === "error") {
				cartStore.setIsRefundableBookingSelected(!isRefundableBookingSelected)
			}
		},

		$applyCode: async (code) => {
			if (isCartEmpty()) {
				return
			}

			$apiHelper.$showLoader()
			const { data, pending, status } = await $cartApiService.$applyCode(
				cartId.value,
				code,
				cartStore.shippingFirstName,
				cartStore.shippingLastName,
				cartStore.shippingEmail
			)

			$apiHelper.$handleResponse(pending, status, data, (response) => {
				const cartData = response.cart
				storeCartMeta(cartData)
				storeCartItems(cartData)
				confetti()
			})
		},

		$removePromoCode: async () => {
			if (isCartEmpty()) {
				return
			}

			$apiHelper.$showLoader()
			const { data, pending, status } = await $cartApiService.$removePromoCode(cartId.value)

			$apiHelper.$handleResponse(pending, status, data, (response) => {
				const cartData = response.cart
				storeCartMeta(cartData)
				storeCartItems(cartData)
			})
		},

		$removeCreditCode: async (code) => {
			if (isCartEmpty()) {
				return
			}

			$apiHelper.$showLoader()
			const { data, pending, status } = await $cartApiService.$removeCreditCode(cartId.value, code)

			$apiHelper.$handleResponse(pending, status, data, (response) => {
				const cartData = response.cart
				storeCartMeta(cartData)
				storeCartItems(cartData)
			})
		},

		//Make sure to switch the currencyStore AFTER calling this method, otherwise no change will be detected
		$updateCartCurrency: async (currencyCode) => {
			if (!cartId.value) {
				//If there is no cart, the user can freely switch currency
				updateCurrencyAndBillingInfo(currencyCode, false)
				$eventBus.$emit(UPDATE_BILLING_COUNTRY)
				return
			}

			const didCurrencyChange = currencyCode !== currency.value.code
			if (didCurrencyChange) {
				$apiHelper.$showLoader()
				const { data, pending, status } = await $cartApiService.$updateCartCurrency(cartId.value, currencyCode)

				$apiHelper.$handleResponse(pending, status, data, (response) => {
					const cartData = response.cart
					storeCartMeta(cartData)
					storeCartItems(cartData)

					const currency = getCartMetaRowFromResponse(cartData).currency
					updateCurrencyAndBillingInfo(currency)
					$eventBus.$emit(UPDATE_BILLING_COUNTRY)
				})
			} else {
				$eventBus.$emit(UPDATE_BILLING_COUNTRY)
			}
		},

		//Make sure to switch the currencyStore AFTER calling this method, otherwise no change will be detected
		$updateSeatSelection: async (holdToken, productSeatSelection) => {
			if (cartId.value) {
				$apiHelper.$showLoader()
				const { data, pending, status } = await $cartApiService.$updateSeatSelection(
					cartId.value,
					holdToken,
					productSeatSelection
				)

				$apiHelper.$handleResponse(pending, status, data, (response) => {
					const cartData = response.cart
					storeCartMeta(cartData)
					storeCartItems(cartData)
					cartStore.setReservationHoldToken(holdToken)
					confetti()
				})
			}
		},

		//Returns whether the server-side cart was actually deleted. Local state is always reset regardless,
		//so a failed delete can never leave a stale cartId behind for the next kiosk user to inherit.
		$clearCart: async () => {
			let didClearOnServer = true

			if (cartId.value) {
				$apiHelper.$showLoader()
				const { status } = await $cartApiService.$clearCart(cartId.value)
				didClearOnServer = status.value === "success"
				$apiHelper.$hideLoader()

				//resetCartDataToDefaults already emits CART_CLEARED itself, resetCartMetaAndItems does not
				if (runtimeConfig.public.IS_KIOSK_APP) {
					await resetCartDataToDefaults()
				} else {
					cartStore.resetCartMetaAndItems()
					$eventBus.$emit(CART_CLEARED)
				}
			} else if (runtimeConfig.public.IS_KIOSK_APP) {
				//Allow kiosk uses to use clear cart button to clear checkout form
				await resetCartDataToDefaults()
			}

			return didClearOnServer
		},

		$resetStoredCartData: resetCartDataToDefaults,
	})
})
